/**
 * Menu de contexto flutuante (o equivalente do `FCRep.BasePopup`): abre na
 * posição do clique, fecha com Esc, com clique fora ou ao escolher um item, e
 * se reposiciona para não sair da janela. Itens com `checked` são opções de
 * escolha única (a marcada fica em destaque); com `toggle` também, são caixas de
 * marcar, cada uma por si, com um ✓ à direita. `icon` vai antes do nome, `hint`
 * é a dica do item e `shortcut`, o atalho, à direita.
 *
 * Um `submenu` abre ao lado (passar o mouse, clicar ou →; ← volta). Um nível só.
 *
 * No celular (docs/spec/mobile.md), o mesmo menu é uma folha de ações presa embaixo, com fundo
 * escuro e itens do tamanho do dedo; o submenu troca a lista da folha, com "voltar" no alto.
 * Quem usa o menu não muda nada.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/index.js';
import { useMobile } from './mobile.js';

export type MenuItem = { kind?: 'item'; label: string; onSelect: () => void; danger?: boolean; disabled?: boolean; icon?: ReactNode; checked?: boolean; toggle?: boolean; hint?: string; shortcut?: string };
export type MenuSubmenu = { kind: 'submenu'; label: string; entries: MenuEntry[]; disabled?: boolean; icon?: ReactNode; hint?: string };

export type MenuEntry = MenuItem | MenuSubmenu | { kind: 'separator' } | { kind: 'title'; label: string };

/**
 * `up`: o menu abre para cima, com o pé em `y` (o botão que o abre fica no rodapé da tela). `end`: o
 * menu fica à esquerda de `x`, com a borda direita nele (o botão fica no canto direito).
 */
export type MenuAnchor = { x: number; y: number; up?: boolean; end?: boolean };

/** Os botões de um painel do menu, na ordem. */
const buttonsOf = (panel: Element | null | undefined) => [...(panel?.querySelectorAll<HTMLButtonElement>(':scope > button:not([disabled])') ?? [])];

export function ContextMenu({ anchor, entries, label, onClose }: { anchor: MenuAnchor; entries: MenuEntry[]; label: string; onClose: () => void }) {
  const t = useT();
  const wrap = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(anchor);
  const sheet = useMobile();
  // o submenu aberto: o índice dele em `entries`, onde ele abre e se o foco vai para dentro
  const [sub, setSub] = useState<{ index: number; x: number; y: number; left: number; focus: boolean } | null>(null);
  const [subPos, setSubPos] = useState<{ x: number; y: number } | null>(null);
  // folha: o fundo só fecha com um toque que começou depois de ela abrir (soltar o dedo depois de
  // segurar solta um clique no lugar do dedo, que agora é o fundo)
  const armed = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!sheet) {
      const { width, height } = el.getBoundingClientRect();
      setPos({
        x: Math.max(4, Math.min(anchor.end ? anchor.x - width : anchor.x, window.innerWidth - width - 4)),
        y: Math.max(4, Math.min(anchor.up ? anchor.y - height : anchor.y, window.innerHeight - height - 4)),
      });
    }
    (el.querySelector<HTMLButtonElement>('[aria-checked="true"]') ?? el.querySelector<HTMLButtonElement>('button:not([disabled])'))?.focus({ preventScroll: true });
  }, [anchor.x, anchor.y, anchor.up, anchor.end, sheet]);

  // o submenu do lado de fora da janela abre do outro lado do item
  useLayoutEffect(() => {
    const el = subRef.current;
    if (!sub || !el) {
      setSubPos(null);
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    const x = sub.x + width > window.innerWidth - 4 ? Math.max(4, sub.left - width) : sub.x;
    setSubPos({ x, y: Math.max(4, Math.min(sub.y, window.innerHeight - height - 4)) });
    if (sub.focus) buttonsOf(el)[0]?.focus({ preventScroll: true });
  }, [sub]);

  // no celular, a lista trocada põe o foco no "voltar"
  useLayoutEffect(() => {
    if (sheet) ref.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true });
  }, [sheet, sub?.index]);

  const openSub = (index: number, button: HTMLElement, focus: boolean) => {
    const rect = button.getBoundingClientRect();
    const panel = ref.current?.getBoundingClientRect();
    setSub({ index, x: (panel?.right ?? rect.right) - 2, y: rect.top - 4, left: (panel?.left ?? rect.left) + 2, focus });
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      const focused = document.activeElement as HTMLButtonElement | null;
      const inSub = !!subRef.current?.contains(focused);
      if (e.key === 'Escape') {
        // o Esc é do menu: não chega à janela (que devolveria um painel ampliado ao lugar)
        e.stopPropagation();
        if (inSub || (sheet && sub)) {
          setSub(null);
          return;
        }
        onClose();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const items = buttonsOf(inSub ? subRef.current : ref.current);
        const at = items.indexOf(focused as HTMLButtonElement);
        const next = items[(at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
        next?.focus();
      }
      if (e.key === 'ArrowRight' && !inSub && !sheet && focused?.dataset.submenu !== undefined) {
        e.preventDefault();
        openSub(Number(focused.dataset.submenu), focused, true);
      }
      if (e.key === 'ArrowLeft' && inSub) {
        e.preventDefault();
        const parent = ref.current?.querySelector<HTMLButtonElement>(`[data-submenu="${sub?.index ?? -1}"]`);
        setSub(null);
        parent?.focus();
      }
    };
    // a folha fecha pelo fundo (abaixo); o menu flutuante, com o clique em qualquer outro lugar
    if (!sheet) document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose, sheet, sub]);

  const render = (list: MenuEntry[], nested: boolean) =>
    list.map((entry, i) => {
      if (entry.kind === 'separator') return <div key={i} className="rd-menu__sep" role="separator" />;
      if (entry.kind === 'title')
        return (
          <div key={i} className="rd-menu__title">
            {entry.label}
          </div>
        );
      const submenu = entry.kind === 'submenu' && !nested;
      const open = submenu && sub?.index === i;
      return (
        <button
          key={i}
          type="button"
          role={entry.kind !== 'submenu' && entry.checked !== undefined ? (entry.toggle ? 'menuitemcheckbox' : 'menuitemradio') : 'menuitem'}
          aria-checked={entry.kind !== 'submenu' ? entry.checked : undefined}
          aria-haspopup={submenu ? 'menu' : undefined}
          aria-expanded={submenu ? open : undefined}
          aria-keyshortcuts={entry.kind !== 'submenu' ? entry.shortcut : undefined}
          data-submenu={submenu ? i : undefined}
          title={entry.hint}
          className={`rd-menu__item${entry.kind !== 'submenu' && entry.danger ? ' rd-menu__item--danger' : ''}${entry.icon ? ' rd-menu__item--icon' : ''}${submenu || (entry.kind !== 'submenu' && (entry.shortcut || entry.toggle)) ? ' rd-menu__item--side' : ''}${entry.kind !== 'submenu' && entry.toggle ? ' rd-menu__item--toggle' : ''}${open ? ' is-open' : ''}`}
          disabled={entry.disabled}
          onMouseEnter={(e) => {
            if (sheet || nested) return;
            if (submenu) openSub(i, e.currentTarget, false);
            else setSub(null);
          }}
          onClick={(e) => {
            if (entry.kind === 'submenu') {
              if (submenu) openSub(i, e.currentTarget, true);
              return;
            }
            onClose();
            entry.onSelect();
          }}
        >
          {entry.icon ? (
            <span className="rd-menu__icon" aria-hidden="true">
              {entry.icon}
            </span>
          ) : null}
          <span className="rd-menu__label">{entry.label}</span>
          {submenu ? (
            <span className="rd-menu__side" aria-hidden="true">
              ›
            </span>
          ) : entry.kind !== 'submenu' && entry.toggle ? (
            <span className="rd-menu__side" aria-hidden="true">
              {entry.checked ? '✓' : ''}
            </span>
          ) : entry.kind !== 'submenu' && entry.shortcut ? (
            // o atalho já vai no aria-keyshortcuts: fora do nome do item
            <kbd className="rd-menu__side" aria-hidden="true">
              {entry.shortcut}
            </kbd>
          ) : null}
        </button>
      );
    });

  const found = sub ? entries[sub.index] : undefined;
  const opened = found?.kind === 'submenu' ? found : null;
  const subEntries = opened?.entries ?? null;

  if (sheet)
    return createPortal(
      <div
        className="rd-actionSheet"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) armed.current = true;
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && armed.current) onClose();
        }}
      >
        <div ref={ref} className="rd-menu rd-actionSheet__panel" role="menu" aria-label={subEntries && opened ? opened.label : label}>
          {subEntries && opened ? (
            <>
              <button type="button" role="menuitem" className="rd-menu__item rd-menu__back" onClick={() => setSub(null)}>
                ‹ {t('common.back')}
              </button>
              <div className="rd-menu__title">{opened.label}</div>
              {render(subEntries, true)}
            </>
          ) : (
            render(entries, false)
          )}
        </div>
      </div>,
      document.body,
    );
  return createPortal(
    <div ref={wrap}>
      <div ref={ref} className="rd-menu rd-contextMenu" role="menu" aria-label={label} style={{ position: 'fixed', left: pos.x, top: pos.y }}>
        {render(entries, false)}
      </div>
      {subEntries && sub && opened ? (
        <div
          ref={subRef}
          className="rd-menu rd-contextMenu rd-contextMenu--sub"
          role="menu"
          aria-label={opened.label}
          style={{ position: 'fixed', left: subPos?.x ?? sub.x, top: subPos?.y ?? sub.y, visibility: subPos ? 'visible' : 'hidden' }}
        >
          {render(subEntries, true)}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
